import * as React from "react";
import { Box, Tooltip, Typography } from "@mui/material";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import type { KitchenArrivalRow } from "./RallyBoardPopup";
import {
  ActiveBadgeFilter,
  RallyBadge,
  // rallyBadgesRef,
  STORY_FILTER_ICON,
  Team_0,
  Team_1,
  teamRgba,
} from "./GameStoryDialog_UPDATED";
import { MatchImpactFacts } from "./GameStory_buildMatchImpactFactsFromInsights";
import zIndex from "@mui/material/styles/zIndex";

// pressure stuff

type Team01 = 0 | 1;

function getPointAwardedTeam_Calc(row: any): 0 | 1 | null {
  // ✅ KitchenArrivalRow preferred
  if (row?.pointAwardedTeam === 0 || row?.pointAwardedTeam === 1)
    return row.pointAwardedTeam;

  // ✅ Current semantics: if server won rally, server’s team scored
  if (
    row?.scoredPoint === true &&
    (row?.servingTeam === 0 || row?.servingTeam === 1)
  ) {
    return row.servingTeam;
  }

  return null;
}

export type ImportanceReason =
  | "no_score_context"
  | "late_game"
  | "close_score"
  | "game_point"
  | "lead_gap"
  | "big_lead_gap"
  | "lead_has_persisted"
  | "score_drought"
  | "big_score_drought"
  | "break_drought_opportunity"
  | "trailing_team_serving"
  | "leading_team_serving";

export type PressureByRally = {
  rallyIndex: number;
  scoreBefore?: [number, number];
  scoreDiff?: number | null;
  leadTeam?: Team01 | null;
  trailingTeam?: Team01 | null;

  leadGapRunLen: number;

  weight: number;
  reasons: ImportanceReason[];

  noPointRunLenBefore: number;
  droughtTeam: 0 | 1 | null;

  pointDroughtByTeam: [number, number];
  pointDroughtByTeamBefore: [number, number];
  lastPointRallyIndexByTeam: [number | null, number | null];
  lastPointRallyIndexByTeamBefore: [number | null, number | null];

  rallyWinDroughtByTeam: [number, number];
  rallyWinDroughtByTeamBefore: [number, number];

  lastRallyWinRallyIndexByTeam: [number | null, number | null];
  lastRallyWinRallyIndexByTeamBefore: [number | null, number | null];
};

const LEAD_GAP_MIN = 3;
const BIG_LEAD_GAP_MIN = 5;
const LEAD_PERSIST_MIN = 3;

const DROUGHT_MIN = 3;
const BIG_DROUGHT_MIN = 6;

function otherTeam(t: Team01): Team01 {
  return t === 0 ? 1 : 0;
}

function leadAndTrailFromScore(scoreBefore: [number, number] | null): {
  diff: number | null;
  leadTeam: Team01 | null;
  trailingTeam: Team01 | null;
} {
  if (!scoreBefore) return { diff: null, leadTeam: null, trailingTeam: null };
  const [a, b] = scoreBefore;
  if (a === b) return { diff: 0, leadTeam: null, trailingTeam: null };
  const leadTeam: Team01 = a > b ? 0 : 1;
  return { diff: Math.abs(a - b), leadTeam, trailingTeam: otherTeam(leadTeam) };
}

// NOTE: this is intentionally “timeline-consistent”: side-out semantics using servingTeam/scoredPoint.
function computeRallyImportance_Timeline(args: {
  serverTeam: Team01 | null;
  scoreBefore: [number, number] | null;
  noPointRunLenBefore: number;
  leadGapRunLen: number;
}): { weight: number; reasons: ImportanceReason[] } {
  const { serverTeam, scoreBefore, noPointRunLenBefore, leadGapRunLen } = args;

  if (!scoreBefore) return { weight: 1, reasons: ["no_score_context"] };

  const [a, b] = scoreBefore;
  const maxScore = Math.max(a, b);
  const diff = Math.abs(a - b);

  let w = 1;
  const reasons: ImportanceReason[] = [];

  if (maxScore >= 8) {
    w += 1;
    reasons.push("late_game");
  }
  if (diff <= 1) {
    w += 1;
    reasons.push("close_score");
  }
  if (a >= 10 || b >= 10) {
    w += 2;
    reasons.push("game_point");
  }

  if (diff >= LEAD_GAP_MIN) {
    w += 1;
    reasons.push("lead_gap");
  }
  if (diff >= BIG_LEAD_GAP_MIN) {
    w += 1;
    reasons.push("big_lead_gap");
  }
  if (diff >= LEAD_GAP_MIN && leadGapRunLen >= LEAD_PERSIST_MIN) {
    w += 1;
    reasons.push("lead_has_persisted");
  }

  if (noPointRunLenBefore >= DROUGHT_MIN) {
    w += 1;
    reasons.push("score_drought");
  }
  if (noPointRunLenBefore >= BIG_DROUGHT_MIN) {
    w += 1;
    reasons.push("big_score_drought");
  }

  if (serverTeam != null) {
    const leadTeam: Team01 | null = a === b ? null : a > b ? 0 : 1;
    const trailingTeam = leadTeam == null ? null : otherTeam(leadTeam);

    if (trailingTeam != null && serverTeam === trailingTeam) {
      w += 1;
      reasons.push("trailing_team_serving");
      if (noPointRunLenBefore >= DROUGHT_MIN) {
        w += 1;
        reasons.push("break_drought_opportunity");
      }
    }
    if (leadTeam != null && serverTeam === leadTeam) {
      reasons.push("leading_team_serving");
    }
  }

  return { weight: Math.min(Math.round(w), 8), reasons };
}

type GlowRailConfig = {
  heights: {
    top: number;
    bottom: number;
  };
  activation: {
    positiveThreshold: number;
    positiveSoftness: number;
    negativeThreshold: number;
    negativeSoftness: number;
  };
  background: {
    showThreshold: number;
    alpha: number;
  };
  fill: {
    innerMinAlpha: number;
    innerMaxAlpha: number;
    outerMinAlpha: number;
    outerMaxAlpha: number;
  };
  glow: {
    spreadMin: number;
    spreadMax: number;
    blurMin: number;
    blurMax: number;
    nearAlphaMin: number;
    nearAlphaMax: number;
    farAlphaMin: number;
    farAlphaMax: number;
  };
  energy: {
    startThreshold: number;
    softness: number;
    maxExtraSpread: number;
    maxExtraBlur: number;
  };
  shape: {
    enableSpikes: boolean;
    spikeStartThreshold: number;
    maxSpikeCount: number;
    minSpikeHeight: number;
    maxSpikeHeight: number;
  };
  band: {
    laneHeight: number;
    gapBelow: number;
    bleedX: number;
    interpolationPower: number;
    blurPx: number;
    spreadEnergyFactor: number;
    spreadValueFactor: number;
    positiveRidgeInset: number;
    negativeRidgeOutset: number;
    centerRidgeBoost: number;
  };
};

const DEFAULT_GLOW_RAIL_CONFIG: GlowRailConfig = {
  heights: {
    top: 5,
    bottom: 5,
  },
  activation: {
    positiveThreshold: 0.32,
    positiveSoftness: 0.16,
    negativeThreshold: 0.24,
    negativeSoftness: 0.16,
  },
  background: {
    showThreshold: 0.12,
    alpha: 0.05,
  },
  fill: {
    innerMinAlpha: 0.06,
    innerMaxAlpha: 0.18,
    outerMinAlpha: 0.04,
    outerMaxAlpha: 0.12,
  },
  glow: {
    spreadMin: 3,
    spreadMax: 10,
    blurMin: 6,
    blurMax: 14,
    nearAlphaMin: 0.18,
    nearAlphaMax: 0.5,
    farAlphaMin: 0.08,
    farAlphaMax: 0.24,
  },
  energy: {
    startThreshold: 0.68,
    softness: 0.18,
    maxExtraSpread: 4,
    maxExtraBlur: 6,
  },
  shape: {
    enableSpikes: false,
    spikeStartThreshold: 0.72,
    maxSpikeCount: 5,
    minSpikeHeight: 1,
    maxSpikeHeight: 6,
  },
  band: {
    laneHeight: 18,
    gapBelow: 4,
    bleedX: 10,
    interpolationPower: 1.35,
    blurPx: 10,
    spreadEnergyFactor: 6,
    spreadValueFactor: 4,
    positiveRidgeInset: 1.1,
    negativeRidgeOutset: 1.1,
    centerRidgeBoost: 1.15,
  },
};

type TeamGlowCharge = {
  positive: number;
  negative: number;
  gamePhase: number;
  droughtPositive: number;
  droughtNegative: number;
  leadPositive: number;
  leadNegative: number;
  runPositive: number;
  runNegative: number;
};

function gamePhaseFactorFromScore(
  scoreBefore?: [number, number] | null,
): number {
  if (!scoreBefore) return 0.22;
  const [a, b] = scoreBefore;
  const maxScore = Math.max(Number(a ?? 0), Number(b ?? 0));
  return clamp01(0.18 + maxScore / 11);
}

function scoringRunIntensityForTeam(p: any, team: Team01): number {
  if (!p) return 0;
  const ownPointDrought =
    Number(
      p?.pointDroughtByTeamBefore?.[team] ?? p?.pointDroughtByTeam?.[team] ?? 0,
    ) || 0;
  const oppPointDrought =
    Number(
      p?.pointDroughtByTeamBefore?.[otherTeam(team)] ??
        p?.pointDroughtByTeam?.[otherTeam(team)] ??
        0,
    ) || 0;

  const ownScoringRun = clamp01((oppPointDrought - 1) / 5);
  const oppScoringRun = clamp01((ownPointDrought - 1) / 5);

  return clamp01(Math.max(ownScoringRun, oppScoringRun));
}

function buildGlowChargeForTeam(
  p: any,
  team: Team01,
  config: GlowRailConfig,
): TeamGlowCharge {
  const gamePhase = gamePhaseFactorFromScore(p?.scoreBefore);

  const ownPointDrought =
    Number(
      p?.pointDroughtByTeamBefore?.[team] ?? p?.pointDroughtByTeam?.[team] ?? 0,
    ) || 0;
  const oppPointDrought =
    Number(
      p?.pointDroughtByTeamBefore?.[otherTeam(team)] ??
        p?.pointDroughtByTeam?.[otherTeam(team)] ??
        0,
    ) || 0;

  const droughtNegativeBase = clamp01((ownPointDrought - 1) / 6);
  const droughtPositiveBase = clamp01((oppPointDrought - 1) / 6) * 0.72;

  const leadGapBase = leadGapIntensity(p);
  const leadPositive = p?.leadTeam === team ? leadGapBase * 0.7 : 0;
  const leadNegative = p?.trailingTeam === team ? leadGapBase : 0;

  const runBase = scoringRunIntensityForTeam(p, team);
  const runPositive = oppPointDrought > ownPointDrought ? runBase * 0.58 : 0;
  const runNegative = ownPointDrought > oppPointDrought ? runBase * 0.58 : 0;

  let positive =
    droughtPositiveBase * 0.34 + leadPositive * 0.42 + runPositive * 0.24;
  let negative =
    droughtNegativeBase * 0.5 + leadNegative * 0.34 + runNegative * 0.16;

  if (p?.scoreBefore?.[0] === p?.scoreBefore?.[1]) {
    positive *= 0.92;
    negative *= 0.92;
  }

  if (hasReason(p, "break_drought_opportunity") && p?.trailingTeam === team) {
    positive = clamp01(positive + 0.16 * gamePhase + 0.06);
  }

  // positive = activateCharge(clamp01(positive * gamePhase), 0.32, 0.16);
  // negative = activateCharge(clamp01(negative * gamePhase), 0.24, 0.16);

  positive = activateCharge(
    clamp01(positive * gamePhase),
    config.activation.positiveThreshold,
    config.activation.positiveSoftness,
  );
  negative = activateCharge(
    clamp01(negative * gamePhase),
    config.activation.negativeThreshold,
    config.activation.negativeSoftness,
  );

  return {
    positive,
    negative,
    gamePhase,
    droughtPositive: clamp01(droughtPositiveBase * gamePhase),
    droughtNegative: clamp01(droughtNegativeBase * gamePhase),
    leadPositive: clamp01(leadPositive * gamePhase),
    leadNegative: clamp01(leadNegative * gamePhase),
    runPositive: clamp01(runPositive * gamePhase),
    runNegative: clamp01(runNegative * gamePhase),
  };
}

function rgbaWithAlpha(color: string, alpha: number): string {
  const m = color.match(/rgba?\(([^)]+)\)/i);
  if (!m) return color;
  const parts = m[1]
    .split(",")
    .map((x) => x.trim())
    .slice(0, 3);
  if (parts.length < 3) return color;
  return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${clamp01(alpha)})`;
}

function railGlowBoxShadow(
  color: string,
  direction: "in" | "out",
  intensity: number,
  config: GlowRailConfig,
  energy = 0,
): string {
  if (intensity <= 0.02) return "none";

  const spreadBase = lerp(
    config.glow.spreadMin,
    config.glow.spreadMax,
    intensity,
  );
  const blurBase = lerp(config.glow.blurMin, config.glow.blurMax, intensity);

  const spread = Math.round(spreadBase + energy * config.energy.maxExtraSpread);
  const blur = Math.round(blurBase + energy * config.energy.maxExtraBlur);

  const near = rgbaWithAlpha(
    color,
    lerp(config.glow.nearAlphaMin, config.glow.nearAlphaMax, intensity),
  );
  const far = rgbaWithAlpha(
    color,
    lerp(config.glow.farAlphaMin, config.glow.farAlphaMax, intensity),
  );

  return direction === "in"
    ? `0 ${spread}px ${blur}px ${near}, 0 ${Math.max(
        1,
        Math.round(spread * 0.45),
      )}px ${Math.max(4, Math.round(blur * 0.55))}px ${far}`
    : `0 -${spread}px ${blur}px ${near}, 0 -${Math.max(
        1,
        Math.round(spread * 0.45),
      )}px ${Math.max(4, Math.round(blur * 0.55))}px ${far}`;
}

export function scorePressureForTeam(p: any, team: 0 | 1): number {
  if (!p) return 0;
  const diff = typeof p?.scoreDiff === "number" ? p.scoreDiff : 0;
  if (diff <= 0) return 0;
  if (p?.trailingTeam === team || p?.leadTeam === team)
    return leadGapIntensity(p);
  return 0;
}

export function buildPressureByRallyIndexFromTimelineRows(
  rows: Array<KitchenArrivalRow>,
) {
  let pointDroughtByTeam: [number, number] = [0, 0];
  let rallyWinDroughtByTeam: [number, number] = [0, 0];

  let lastPointRallyIndexByTeam: [number | null, number | null] = [null, null];
  let lastRallyWinRallyIndexByTeam: [number | null, number | null] = [
    null,
    null,
  ];

  let noPointRunLen = 0;
  let prevLeadTeam: Team01 | null = null;
  let leadGapRunLen = 0;

  const out: Record<number, PressureByRally> = {};

  for (const r of rows) {
    const rallyIndex: number = r.rallyIndex;

    const scoreBefore: [number, number] | null = Array.isArray(r.scoreBefore)
      ? [Number(r.scoreBefore[0] ?? 0), Number(r.scoreBefore[1] ?? 0)]
      : null;

    const { diff, leadTeam, trailingTeam } = leadAndTrailFromScore(scoreBefore);

    const isLeadGapState =
      diff != null && diff >= LEAD_GAP_MIN && leadTeam != null;

    if (isLeadGapState && leadTeam === prevLeadTeam) leadGapRunLen += 1;
    else if (isLeadGapState) leadGapRunLen = 1;
    else leadGapRunLen = 0;

    const serverTeam: Team01 | null =
      r.servingTeam === 0 || r.servingTeam === 1 ? r.servingTeam : null;

    const imp = computeRallyImportance_Timeline({
      serverTeam,
      scoreBefore,
      noPointRunLenBefore: noPointRunLen,
      leadGapRunLen,
    });

    out[rallyIndex] = {
      rallyIndex,
      scoreBefore: scoreBefore ?? undefined,
      scoreDiff: diff,
      leadTeam,
      trailingTeam,
      leadGapRunLen,
      weight: imp.weight,
      reasons: imp.reasons,
      noPointRunLenBefore: noPointRunLen,
      droughtTeam:
        pointDroughtByTeam[0] === pointDroughtByTeam[1]
          ? null
          : pointDroughtByTeam[0] > pointDroughtByTeam[1]
            ? 0
            : 1,

      pointDroughtByTeamBefore: [...pointDroughtByTeam] as [number, number],
      lastPointRallyIndexByTeamBefore: [...lastPointRallyIndexByTeam] as [
        number | null,
        number | null,
      ],

      rallyWinDroughtByTeamBefore: [...rallyWinDroughtByTeam] as [
        number,
        number,
      ],
      lastRallyWinRallyIndexByTeamBefore: [...lastRallyWinRallyIndexByTeam] as [
        number | null,
        number | null,
      ],

      pointDroughtByTeam: [0, 0],
      lastPointRallyIndexByTeam: [null, null],
      rallyWinDroughtByTeam: [0, 0],
      lastRallyWinRallyIndexByTeam: [null, null],
    };

    const pointAwardedTeam = getPointAwardedTeam_Calc(r);
    const rallyWinnerTeam: Team01 | null =
      r.winnerTeam === 0 || r.winnerTeam === 1 ? r.winnerTeam : null;

    if (pointAwardedTeam == null) {
      noPointRunLen += 1;
      pointDroughtByTeam = [
        pointDroughtByTeam[0] + 1,
        pointDroughtByTeam[1] + 1,
      ];
    } else {
      noPointRunLen = 0;
      pointDroughtByTeam[pointAwardedTeam] = 0;
      pointDroughtByTeam[otherTeam(pointAwardedTeam)] += 1;
      lastPointRallyIndexByTeam[pointAwardedTeam] = rallyIndex;
    }

    if (rallyWinnerTeam != null) {
      rallyWinDroughtByTeam[rallyWinnerTeam] = 0;
      rallyWinDroughtByTeam[otherTeam(rallyWinnerTeam)] += 1;
      lastRallyWinRallyIndexByTeam[rallyWinnerTeam] = rallyIndex;
    } else {
      rallyWinDroughtByTeam = [
        rallyWinDroughtByTeam[0] + 1,
        rallyWinDroughtByTeam[1] + 1,
      ];
    }

    out[rallyIndex].pointDroughtByTeam = [...pointDroughtByTeam] as [
      number,
      number,
    ];
    out[rallyIndex].lastPointRallyIndexByTeam = [
      ...lastPointRallyIndexByTeam,
    ] as [number | null, number | null];
    out[rallyIndex].rallyWinDroughtByTeam = [...rallyWinDroughtByTeam] as [
      number,
      number,
    ];
    out[rallyIndex].lastRallyWinRallyIndexByTeam = [
      ...lastRallyWinRallyIndexByTeam,
    ] as [number | null, number | null];

    prevLeadTeam = isLeadGapState ? leadTeam : null;
  }

  return out;
}

// end pressure stuff

// glow rails

export function reasonLabel(r: string) {
  if (r === "break_drought_opportunity") return "break chance";
  if (r === "trailing_team_serving") return "trailing serving";
  if (r === "lead_has_persisted") return "persisted lead";
  if (r === "big_score_drought") return "big drought";
  if (r === "big_lead_gap") return "big gap";
  if (r === "score_drought") return "drought";
  if (r === "lead_gap") return "gap";
  return null;
}

export function filteredDroughtReasons(p: any): string[] {
  const keep = new Set([
    "score_drought",
    "big_score_drought",
    "trailing_team_serving",
    "break_drought_opportunity",
  ]);
  return (p?.reasons ?? [])
    .filter((x: string) => keep.has(x))
    .map((x: string) => reasonLabel(x))
    .filter(Boolean) as string[];
}

export function filteredLeadReasons(p: any): string[] {
  const keep = new Set(["lead_gap", "big_lead_gap", "lead_has_persisted"]);
  return (p?.reasons ?? [])
    .filter((x: string) => keep.has(x))
    .map((x: string) => reasonLabel(x))
    .filter(Boolean) as string[];
}

export function teamName(t: any) {
  return t === 0 ? "Team 0" : t === 1 ? "Team 1" : "—";
}

export function ServePaddleIcon(props: {
  color: string;
  dim?: boolean; /** Pixel width of the icon. Height defaults to width * 10/16 to preserve aspect. */
  width?: number;
  /** Optional explicit height. If omitted, derived from width with 16:10 ratio. */
  height?: number;
}) {
  const { color, dim, width = 16, height = (width * 10) / 16 } = props;

  const h = height ?? Math.round((width * 10) / 16); // keep same aspect ratio

  return (
    <Box
      sx={{
        width: width,
        height: h,
        //height: 10,
        opacity: dim ? 0.35 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        userSelect: "none",
        paddingLeft: "1px",
      }}
    >
      <svg width={width} height={h} viewBox="0 0 32 20" aria-hidden="true">
        {/* handle to the LEFT */}
        <rect x="2" y="8" width="8" height="4" rx="2" fill={color} />

        {/* paddle face (horizontal) on the RIGHT */}
        <rect x="10" y="4" width="16" height="12" rx="6" fill={color} />

        {/* subtle outline around the face */}
        <rect
          x="10"
          y="4"
          width="16"
          height="12"
          rx="6"
          fill="none"
          stroke="rgba(0,0,0,0.35)"
          strokeWidth="1"
        />
      </svg>
    </Box>
  );
}

const iconChip = (team: 0 | 1) => ({
  width: 16,
  height: 16,
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: team === 0 ? "rgba(60,130,255,0.18)" : "rgba(80,220,140,0.18)",
  backdropFilter: "blur(2px)",
});

export function clamp01(x: number) {
  return Math.max(0, Math.min(1, x));
}

export function activateCharge(
  value: number,
  threshold = 0.26,
  softness = 0.18,
) {
  const v = clamp01(value);
  if (v <= threshold) return 0;
  const t = clamp01((v - threshold) / Math.max(0.0001, softness));
  return t * t * (3 - 2 * t); // smoothstep
}

export function chargeEnergy(value: number, threshold = 0.68, softness = 0.18) {
  const v = clamp01(value);
  if (v <= threshold) return 0;
  const t = clamp01((v - threshold) / Math.max(0.0001, softness));
  return t * t * (3 - 2 * t);
}

function smoothBandValue(value: number, power = 1.35) {
  return Math.pow(clamp01(value), power);
}

function buildBandStopColor(color: string, alpha: number) {
  return rgbaWithAlpha(color, clamp01(alpha));
}

function buildSharedTensionColor(shared: number) {
  if (shared <= 0.01) return "transparent";

  if (shared < 0.45) {
    return `rgba(255, 215, 96, ${lerp(0.12, 0.28, shared / 0.45)})`;
  }
  if (shared < 0.75) {
    const t = (shared - 0.45) / 0.3;
    return `rgba(255, ${Math.round(196 - 90 * t)}, 48, ${lerp(0.28, 0.5, t)})`;
  }

  const t = (shared - 0.75) / 0.25;
  return `rgba(255, ${Math.round(106 - 46 * t)}, ${Math.round(
    48 - 24 * t,
  )}, ${lerp(0.5, 0.72, clamp01(t))})`;
}

function buildBandRidgePoints(args: {
  centers: Array<{
    x: number;
    width: number;
    positive: number;
    negative: number;
    energy: number;
  }>;
  bandType: "top" | "bottom";
  laneHeight: number;
  seamGap: number;
}) {
  const { centers, bandType, laneHeight, seamGap } = args;

  const seamY = bandType === "top" ? laneHeight : laneHeight + seamGap;

  return centers.map((c) => {
    const positive = clamp01(c.positive);
    const negative = clamp01(c.negative);
    const value = Math.max(positive, negative);
    const polarity = positive >= negative ? 1 : -1;
    const energy = clamp01(c.energy);

    const gated =
      value <= 0.12 ? 0 : smoothBandValue((value - 0.12) / 0.88, 1.7);

    if (gated <= 0.001) {
      return {
        x: c.x,
        y: seamY,
        strength: 0,
        polarity,
        energy: 0,
      };
    }

    const seamPull =
      polarity > 0
        ? lerp(0.75, laneHeight * 0.42, gated)
        : lerp(0.2, laneHeight * 0.14, gated);

    const energyLift = lerp(0, 0.9, energy) * gated;
    const offset = seamPull + energyLift;

    const y = bandType === "top" ? seamY - offset : seamY + offset;

    return {
      x: c.x,
      y,
      strength: gated,
      polarity,
      energy,
    };
  });
}
function buildBandRidgePoints_old(args: {
  centers: Array<{
    x: number;
    width: number;
    positive: number;
    negative: number;
    energy: number;
  }>;
  bandType: "top" | "bottom";
  laneHeight: number;
  seamGap: number;
}) {
  const { centers, bandType, laneHeight, seamGap } = args;

  const seamY = bandType === "top" ? laneHeight : laneHeight + seamGap;

  return centers.map((c) => {
    const positive = clamp01(c.positive);
    const negative = clamp01(c.negative);
    const value = Math.max(positive, negative);
    const polarity = positive >= negative ? 1 : -1;
    const energy = clamp01(c.energy);

    const strength = smoothBandValue(value, 1.35);

    const seamPull =
      polarity > 0
        ? lerp(1.25, laneHeight * 0.52, strength)
        : lerp(0.4, laneHeight * 0.18, strength);

    const energyLift = lerp(0, 1.4, energy);

    const offset = seamPull + energyLift;

    const y = bandType === "top" ? seamY - offset : seamY + offset;

    return {
      x: c.x,
      y,
      strength,
      polarity,
      energy,
    };
  });
}

function buildSmoothPathD(
  points: Array<{
    x: number;
    y: number;
    strength: number;
    polarity: number;
    energy: number;
  }>,
) {
  if (!points.length) return "";

  let d = "";

  let i = 0;
  while (i < points.length) {
    while (i < points.length && points[i].strength <= 0.001) i += 1;
    if (i >= points.length) break;

    const segmentStart = i;
    while (i < points.length && points[i].strength > 0.001) i += 1;
    const segment = points.slice(segmentStart, i);

    if (!segment.length) continue;

    if (segment.length === 1) {
      const p = segment[0];
      d += ` M ${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
      continue;
    }

    d += ` M ${segment[0].x.toFixed(2)} ${segment[0].y.toFixed(2)}`;

    for (let j = 0; j < segment.length - 1; j += 1) {
      const p0 = segment[j];
      const p1 = segment[j + 1];
      const midX = (p0.x + p1.x) / 2;
      const midY = (p0.y + p1.y) / 2;

      d += ` Q ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} ${midX.toFixed(
        2,
      )} ${midY.toFixed(2)}`;
    }

    const last = segment[segment.length - 1];
    d += ` T ${last.x.toFixed(2)} ${last.y.toFixed(2)}`;
  }

  return d.trim();
}

function buildSmoothPathD_old(
  points: Array<{
    x: number;
    y: number;
    strength: number;
    polarity: number;
    energy: number;
  }>,
) {
  if (!points.length) return "";
  if (points.length === 1) {
    const p = points[0];
    return `M ${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
  }

  let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const midX = (p0.x + p1.x) / 2;
    const midY = (p0.y + p1.y) / 2;

    d += ` Q ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} ${midX.toFixed(
      2,
    )} ${midY.toFixed(2)}`;
  }

  const last = points[points.length - 1];
  d += ` T ${last.x.toFixed(2)} ${last.y.toFixed(2)}`;

  return d;
}

function buildPressureBandBackground(args: {
  centers: Array<{
    x: number;
    width: number;
    positive: number;
    negative: number;
    energy: number;
  }>;
  color: string;
  laneHeight: number;
  bleedX: number;
  interpolationPower: number;
  blurPx: number;
  bandType: "top" | "bottom";
  config: GlowRailConfig;
}) {
  const {
    centers,
    color,
    laneHeight,
    bleedX,
    interpolationPower,
    blurPx,
    bandType,
    config,
  } = args;

  if (!centers.length) {
    return {
      background: "transparent",
      filter: "none",
    };
  }

  const parts: string[] = [];

  centers.forEach((c) => {
    const polarity = c.positive >= c.negative ? 1 : -1;
    const value = clamp01(polarity > 0 ? c.positive : c.negative);
    if (value <= 0.01) return;

    const v = smoothBandValue(value, interpolationPower);
    const energy = clamp01(c.energy);

    const coreAlpha = lerp(0.1, 0.38, v);

    const ridgeAlpha = polarity > 0 ? lerp(0.2, 0.62, v) : lerp(0.1, 0.38, v);

    const outerAlpha = lerp(0.02, 0.1, v);

    const spread =
      c.width / 2 +
      bleedX +
      energy * config.band.spreadEnergyFactor +
      value * config.band.spreadValueFactor;

    const x = c.x;

    const ridgeBase = bandType === "top" ? 100 : 0;
    const ridgeOffset =
      polarity > 0
        ? -laneHeight * config.band.positiveRidgeInset
        : laneHeight * config.band.negativeRidgeOutset;

    const ridgeY = `${ridgeBase + ridgeOffset}%`;

    parts.push(
      `radial-gradient(ellipse ${spread}px ${laneHeight + energy * 4}px at ${x}px ${ridgeY},
        ${buildBandStopColor(color, ridgeAlpha * config.band.centerRidgeBoost)} 0%,
        ${buildBandStopColor(color, coreAlpha)} 22%,
        ${buildBandStopColor(color, outerAlpha)} 55%,
        ${buildBandStopColor(color, 0)} 100%)`,
    );
  });

  return {
    background: parts.length ? parts.join(", ") : "transparent",
    filter: `blur(${blurPx}px)`,
  };
}

function buildPressureBandBackground_old(args: {
  centers: Array<{
    x: number;
    width: number;
    positive: number;
    negative: number;
    energy: number;
  }>;
  color: string;
  laneHeight: number;
  bleedX: number;
  interpolationPower: number;
  blurPx: number;
  bandType: "top" | "bottom";
}) {
  const {
    centers,
    color,
    laneHeight,
    bleedX,
    interpolationPower,
    blurPx,
    bandType,
  } = args;

  if (!centers.length) {
    return {
      background: "transparent",
      filter: "none",
    };
  }

  const parts: string[] = [];

  centers.forEach((c) => {
    const value = clamp01(Math.max(c.positive, c.negative));
    if (value <= 0.01) return;

    const v = smoothBandValue(value, interpolationPower);
    const energy = clamp01(c.energy);

    const coreAlpha = lerp(0.08, 0.34, v);
    const outerAlpha = lerp(0.03, 0.14, v);
    const spread = c.width / 2 + bleedX + energy * 10;

    const x0 = Math.max(0, c.x - spread);
    const x1 = c.x;
    const x2 = c.x + spread;

    const ridgeY =
      bandType === "top"
        ? `${Math.max(10, 100 - laneHeight * 1.8)}%`
        : `${Math.min(90, laneHeight * 1.8)}%`;

    parts.push(
      `radial-gradient(ellipse ${spread}px ${laneHeight + energy * 8}px at ${x1}px ${ridgeY},
        ${buildBandStopColor(color, coreAlpha)} 0%,
        ${buildBandStopColor(color, outerAlpha)} 48%,
        ${buildBandStopColor(color, 0)} 100%)`,
    );
  });

  return {
    background: parts.length ? parts.join(", ") : "transparent",
    filter: `blur(${blurPx}px)`,
  };
}

function buildPressureBandCenters(args: {
  rows: KitchenArrivalRow[];
  pressureByRallyIndex: Record<number, any>;
  glowRailConfig: GlowRailConfig;
  colW: number;
  colGap: number;
}) {
  const { rows, pressureByRallyIndex, glowRailConfig, colW, colGap } = args;

  return rows.map((r, index) => {
    const p = pressureByRallyIndex?.[r.rallyIndex];
    const team0Charge = buildGlowChargeForTeam(p, 0, glowRailConfig);
    const team1Charge = buildGlowChargeForTeam(p, 1, glowRailConfig);

    const x = index * (colW + colGap) + colW / 2;

    return {
      rallyIndex: r.rallyIndex,
      x,
      width: colW,
      team0: {
        positive: team0Charge.positive,
        negative: team0Charge.negative,
        energy: chargeEnergy(
          Math.max(team0Charge.positive, team0Charge.negative),
          glowRailConfig.energy.startThreshold,
          glowRailConfig.energy.softness,
        ),
      },
      team1: {
        positive: team1Charge.positive,
        negative: team1Charge.negative,
        energy: chargeEnergy(
          Math.max(team1Charge.positive, team1Charge.negative),
          glowRailConfig.energy.startThreshold,
          glowRailConfig.energy.softness,
        ),
      },
    };
  });
}

export function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

export function leadGapIntensity(p: any): number {
  if (!p) return 0;
  const diff = typeof p.scoreDiff === "number" ? p.scoreDiff : 0;
  const run = typeof p.leadGapRunLen === "number" ? p.leadGapRunLen : 0;

  // diff 3 feels real; 5 big; normalize roughly
  const diffT = clamp01((diff - 2) / 6); // 2->0, 8->1
  const runT = clamp01(run / 6) * 0.35; // gentle persistence boost
  return clamp01(diffT + runT);
}

export function droughtIntensity(p: any): number {
  if (!p) return 0;
  const n =
    typeof p.noPointRunLenBefore === "number" ? p.noPointRunLenBefore : 0;
  // 3 feels real; 6 big
  return clamp01((n - 2) / 6);
}

export const LEAD_REASONS: ImportanceReason[] = [
  "lead_gap",
  "big_lead_gap",
  "lead_has_persisted",
];

export const DROUGHT_REASONS: ImportanceReason[] = [
  "score_drought",
  "big_score_drought",
  "break_drought_opportunity",
  // optional, but usually relevant to “drought pressure”
  "trailing_team_serving",
];

export function reasonsFor(p: any, allow: string[]) {
  const rs: string[] = Array.isArray(p?.reasons) ? p.reasons : [];
  return rs.filter((r) => allow.includes(r));
}

export function teamLabel(t: any) {
  if (t === 0) return "Team 0";
  if (t === 1) return "Team 1";
  return "—";
}

export function reasonsForLeadGap(p: any): ImportanceReason[] {
  const rs: ImportanceReason[] = Array.isArray(p?.reasons) ? p.reasons : [];
  const allow = new Set(["lead_gap", "big_lead_gap", "lead_has_persisted"]);
  return rs.filter((r) => allow.has(r));
}

export function reasonsForDrought(p: any): ImportanceReason[] {
  const rs: ImportanceReason[] = Array.isArray(p?.reasons) ? p.reasons : [];
  const allow = new Set([
    "score_drought",
    "big_score_drought",
    "break_drought_opportunity",
    "trailing_team_serving",
    "leading_team_serving",
  ]);
  return rs.filter((r) => allow.has(r));
}

export function hasReason(p: any, reason: string) {
  return Array.isArray(p?.reasons) && p.reasons.includes(reason);
}

const GLOW_RAIL_CONFIG_STORAGE_KEY =
  "poc2-condensed-rally-timeline-glow-rail-config-v1";

function mergeGlowRailConfig(
  base: GlowRailConfig,
  patch?: Partial<GlowRailConfig> | null,
): GlowRailConfig {
  return {
    ...base,
    ...patch,
    heights: {
      ...base.heights,
      ...(patch?.heights ?? {}),
    },
    activation: {
      ...base.activation,
      ...(patch?.activation ?? {}),
    },
    background: {
      ...base.background,
      ...(patch?.background ?? {}),
    },
    fill: {
      ...base.fill,
      ...(patch?.fill ?? {}),
    },
    glow: {
      ...base.glow,
      ...(patch?.glow ?? {}),
    },
    energy: {
      ...base.energy,
      ...(patch?.energy ?? {}),
    },
    shape: {
      ...base.shape,
      ...(patch?.shape ?? {}),
    },
    band: {
      ...base.band,
      ...(patch?.band ?? {}),
    },
  };
}

function parseStoredGlowRailConfig(): GlowRailConfig | null {
  try {
    const raw = window.localStorage.getItem(GLOW_RAIL_CONFIG_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return mergeGlowRailConfig(DEFAULT_GLOW_RAIL_CONFIG, parsed);
  } catch {
    return null;
  }
}

function formatGlowRailConfigForConsole(config: GlowRailConfig) {
  return JSON.stringify(config, null, 2);
}

export function CondensedRallyTimeline(props: {
  rows: KitchenArrivalRow[];
  activeRallyIndex: number | null;
  onPickRally: (rallyIndex: number) => void;

  // for sync scrolling
  registerRef?: (rallyIndex: number, el: HTMLDivElement | null) => void;
  /** Height (px) of the score bars area (not including the legend row). */
  barsAreaHeightPx: number;
  matchImpactFacts: MatchImpactFacts | null;
  includedRallies?: Set<number> | null; // ✅ NEW
  onHoverBadge?: (b: RallyBadge | null) => void;
  activeBadgeFilter?: ActiveBadgeFilter | null;
  showFirefightIcon?: boolean; // default true
  firefightRallySet: Set<number>; // rallyIndex -> has firefight
  firefightIcon?: string; // default "🔥"
  rallyBadgesRef: React.MutableRefObject<Map<number, RallyBadge[]>>;
}) {
  const DIM_OPACITY = 0.32; // tweak
  const DIM_GRAYSCALE = "grayscale(55%)";

  const BADGES_H = 16;
  const BADGES_MAX = 6; // extras become +N

  const {
    rows,
    activeRallyIndex,
    onPickRally,
    registerRef,
    barsAreaHeightPx,
    includedRallies,
    onHoverBadge,
    activeBadgeFilter,
    showFirefightIcon = true,
    firefightRallySet = new Set(),
    firefightIcon = "🔥",
    matchImpactFacts,
    rallyBadgesRef,
  } = props;

  // const pressureByRallyIndex = matchImpactFacts?.pressureByRallyIndex;

  //

  //

  const pressureByRallyIndex = React.useMemo(() => {
    return buildPressureByRallyIndexFromTimelineRows(rows);
  }, [rows]);

  const localRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());

  const LABEL_H = 16; // reserved space at top for score label
  const GAP_H = 10; // bottom padding you already effectively have

  const boxShadowRallyWinner = "none"; //"0 0 0 1px rgba(255,193,7,0.90) inset";

  const barsH = Math.max(
    60,
    Math.min(260, Math.round(barsAreaHeightPx ?? 108)),
  );
  const barsRowH = Math.max(20, barsH - LABEL_H - GAP_H);
  const FOOTER_H = 34; // tweak: paddle + 2 tiny lines
  const colH = barsH + 48 + 15 + FOOTER_H; // total column height

  // const TOP_RAIL_H = 5;
  // const BOTTOM_RAIL_H = 5;

  // const glowRailConfig = DEFAULT_GLOW_RAIL_CONFIG;

  const [showGlowRailDevPopup, setShowGlowRailDevPopup] = React.useState(false);

  const [glowRailConfig, setGlowRailConfig] = React.useState<GlowRailConfig>(
    () => {
      if (typeof window === "undefined") return DEFAULT_GLOW_RAIL_CONFIG;
      return parseStoredGlowRailConfig() ?? DEFAULT_GLOW_RAIL_CONFIG;
    },
  );

  function updateGlowRailConfig(
    updater: (prev: GlowRailConfig) => GlowRailConfig,
  ) {
    setGlowRailConfig((prev) => updater(prev));
  }

  function setGlowRailNumber(
    path:
      | "heights.top"
      | "heights.bottom"
      | "activation.positiveThreshold"
      | "activation.positiveSoftness"
      | "activation.negativeThreshold"
      | "activation.negativeSoftness"
      | "background.showThreshold"
      | "background.alpha"
      | "glow.nearAlphaMax"
      | "glow.farAlphaMax"
      | "energy.startThreshold"
      | "band.laneHeight"
      | "band.bleedX"
      | "band.interpolationPower"
      | "band.blurPx",

    value: number,
  ) {
    updateGlowRailConfig((prev) => {
      const next = mergeGlowRailConfig(prev, {});
      const parts = path.split(".");
      const a = parts[0] as keyof GlowRailConfig;
      const b = parts[1] as string;

      return {
        ...next,
        [a]: {
          ...(next[a] as any),
          [b]: value,
        },
      } as GlowRailConfig;
    });
  }

  function resetGlowRailConfig() {
    setGlowRailConfig(DEFAULT_GLOW_RAIL_CONFIG);
  }

  function logGlowRailConfig() {
// console.error(formatGlowRailConfigForConsole(glowRailConfig));
  }

  const COL_W = 22;
  const COL_GAP = 8;

  const pressureBandCenters = React.useMemo(() => {
    return buildPressureBandCenters({
      rows,
      pressureByRallyIndex,
      glowRailConfig,
      colW: COL_W,
      colGap: COL_GAP,
    });
  }, [rows, pressureByRallyIndex, glowRailConfig]);

  const topBandStyle = React.useMemo(() => {
    return buildPressureBandBackground({
      centers: pressureBandCenters.map((c) => ({
        x: c.x,
        width: c.width,
        positive: c.team0.positive,
        negative: c.team0.negative,
        energy: c.team0.energy,
      })),
      color: teamRgba(Team_0, 1),
      laneHeight: glowRailConfig.band.laneHeight,
      bleedX: glowRailConfig.band.bleedX,
      interpolationPower: glowRailConfig.band.interpolationPower,
      blurPx: glowRailConfig.band.blurPx,
      bandType: "top",
      config: glowRailConfig,
    });
  }, [pressureBandCenters, glowRailConfig]);

  const bottomBandStyle = React.useMemo(() => {
    return buildPressureBandBackground({
      centers: pressureBandCenters.map((c) => ({
        x: c.x,
        width: c.width,
        positive: c.team1.positive,
        negative: c.team1.negative,
        energy: c.team1.energy,
      })),
      color: teamRgba(Team_1, 1),
      laneHeight: glowRailConfig.band.laneHeight,
      bleedX: glowRailConfig.band.bleedX,
      interpolationPower: glowRailConfig.band.interpolationPower,
      blurPx: glowRailConfig.band.blurPx,
      bandType: "bottom",
      config: glowRailConfig,
    });
  }, [pressureBandCenters, glowRailConfig]);

  const sharedTensionStyle = React.useMemo(() => {
    const parts: string[] = [];

    pressureBandCenters.forEach((c) => {
      const shared = clamp01(Math.min(c.team0.negative, c.team1.negative));
      if (shared <= 0.08) return;

      const spread =
        c.width / 2 +
        glowRailConfig.band.bleedX * 0.85 +
        c.team0.energy * 3 +
        c.team1.energy * 3 +
        shared * 8;

      const x = c.x;
      const seamY = "50%";
      const color = buildSharedTensionColor(shared);

      parts.push(
        `radial-gradient(ellipse ${spread}px 11px at ${x}px ${seamY},
        ${color} 0%,
        rgba(255,180,64,0.10) 38%,
        rgba(255,160,64,0.00) 100%)`,
      );
    });

    return {
      background: parts.length ? parts.join(", ") : "transparent",
      filter: "blur(0.6px)",
    };
  }, [pressureBandCenters, glowRailConfig]);

  const topRidgePoints = React.useMemo(() => {
    return buildBandRidgePoints({
      centers: pressureBandCenters.map((c) => ({
        x: c.x,
        width: c.width,
        positive: c.team0.positive,
        negative: c.team0.negative,
        energy: c.team0.energy,
      })),
      bandType: "top",
      laneHeight: glowRailConfig.band.laneHeight,
      seamGap: 2,
    });
  }, [pressureBandCenters, glowRailConfig]);

  const bottomRidgePoints = React.useMemo(() => {
    return buildBandRidgePoints({
      centers: pressureBandCenters.map((c) => ({
        x: c.x,
        width: c.width,
        positive: c.team1.positive,
        negative: c.team1.negative,
        energy: c.team1.energy,
      })),
      bandType: "bottom",
      laneHeight: glowRailConfig.band.laneHeight,
      seamGap: 2,
    });
  }, [pressureBandCenters, glowRailConfig]);

  const topRidgePathD = React.useMemo(() => {
    return buildSmoothPathD(topRidgePoints);
  }, [topRidgePoints]);

  const bottomRidgePathD = React.useMemo(() => {
    return buildSmoothPathD(bottomRidgePoints);
  }, [bottomRidgePoints]);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        GLOW_RAIL_CONFIG_STORAGE_KEY,
        JSON.stringify(glowRailConfig),
      );
    } catch {}
  }, [glowRailConfig]);

  React.useEffect(() => {
    if (activeRallyIndex == null) return;
    const el = localRefs.current.get(activeRallyIndex);
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    });
  }, [activeRallyIndex]);

  const perRally = React.useMemo(() => {
    // If you ever have multiple segments per rally, keep first per rallyIndex.
    const m = new Map<number, KitchenArrivalRow>();
    for (const r of rows) {
      if (!m.has(r.rallyIndex)) m.set(r.rallyIndex, r);
    }

    const base = Array.from(m.values()).sort(
      (a, b) => a.rallyIndex - b.rallyIndex,
    );

    const items = base.map((r) => {
      const before = Array.isArray(r.scoreBefore) ? r.scoreBefore : [0, 0];
      const after = Array.isArray(r.scoreAfter) ? r.scoreAfter : before;

      const beforeLabel = `${before[0]}-${before[1]}`;
      const afterLabel = `${after[0]}-${after[1]}`;

      const changed = r.scoredPoint === true; // backend truth
      const displayScoreLabel = changed ? afterLabel : beforeLabel;
      const scoreFaded = !changed;

      const numShots =
        typeof (r as any)?.numShots === "number"
          ? (r as any).numShots
          : Array.isArray((r as any)?.shots)
            ? (r as any).shots.length
            : typeof (r as any)?.shotsCount === "number"
              ? (r as any).shotsCount
              : null;

      return { r, after, displayScoreLabel, scoreFaded, numShots };

      // return { r, after, displayScoreLabel, scoreFaded };
    });

    const maxScoreEnd = items.reduce((mx, it) => {
      const a0 = Number(it.after?.[0] ?? 0);
      const a1 = Number(it.after?.[1] ?? 0);
      return Math.max(mx, a0, a1);
    }, 0);

    const maxShots = items.reduce((mx, it) => {
      const n = typeof it.numShots === "number" ? it.numShots : 0;
      return Math.max(mx, n);
    }, 0);

    return { items, maxScoreEnd, maxShots };
  }, [rows]);

  // React.useEffect(() => {
  //   console.error("perRally", perRally);
  // }, [perRally]);

  // function getPointAwardedTeam_Calc(row: any): 0 | 1 | null {
  //   // ✅ KitchenArrivalRow preferred
  //   if (row?.pointAwardedTeam === 0 || row?.pointAwardedTeam === 1)
  //     return row.pointAwardedTeam;

  //   // ✅ Current semantics: if server won rally, server’s team scored
  //   if (
  //     row?.scoredPoint === true &&
  //     (row?.servingTeam === 0 || row?.servingTeam === 1)
  //   ) {
  //     return row.servingTeam;
  //   }

  //   return null;
  // }

  // const goldCap = {
  //   position: "absolute" as const,
  //   left: 0,
  //   right: 0,
  //   top: 0,
  //   height: 4,
  //   borderRadius: 999,
  //   background:
  //     "linear-gradient(180deg, rgba(255,235,160,0.95) 0%, rgba(255,204,90,0.30) 70%, rgba(255,204,90,0) 100%)",
  //   boxShadow: "0 0 8px rgba(255,210,90,0.35)",
  //   pointerEvents: "none" as const,
  // };

  const GOLD_CAP_H = 0; //6; // taller
  const goldCapBg =
    "linear-gradient(180deg, rgba(255,232,160,0.98) 0%, rgba(224,170,78,0.98) 55%, rgba(176,120,34,0.98) 100%)";
  const goldGlow = "0 0 10px rgba(224,170,78,0.55)";

  rallyBadgesRef.current.forEach((arr, ri) => {
    const seen = new Set<string>();
    const dedup: RallyBadge[] = [];

    for (const b of arr) {
      const sig = `${b.stake}|${b.key}|${b.team}`;
      if (seen.has(sig)) continue;
      seen.add(sig);
      dedup.push(b);
    }

    rallyBadgesRef.current.set(ri, dedup);
  });

  return (
    <Box
      sx={{
        // border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 2,
        overflowX: "auto",
        whiteSpace: "nowrap",
        marginTop: "6px",
        marginLeft: "6px",
      }}
    >
      <Box>
        <span data-team="0">
          <Box
            sx={{
              width: "20px",
              height: "8px",
              borderRadius: "2px",
              background: teamRgba(Team_0, 0.7), //  "rgba(80,160,255,0.70)",
              display: "inline-block",
              marginLeft: 1,
              marginRight: "4px",
            }}
          />
          <Box sx={{ display: "inline-block" }}>{"Team 0"}</Box>
        </span>

        <Box sx={{ display: "inline-block", width: "16px" }} />

        <span data-team="1">
          <Box
            sx={{
              width: "20px",
              height: "8px",
              borderRadius: "2px",
              background: teamRgba(Team_1, 0.65), // "rgba(120,220,140,0.65)",
              display: "inline-block",
              marginLeft: 1,
              marginRight: "4px",
            }}
          />
          <Box sx={{ display: "inline-block", marginLeft: "1px" }}>
            {"Team 1"}
          </Box>
        <Box sx={{ display: "inline-block", marginLeft: "16px" , fontStyle: "italic",}} >
          Navigate to a rally by clicking on its box below
          </Box>
        </span>
      </Box>

      {/* <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
        <Box
          onClick={() => setShowGlowRailDevPopup((v) => !v)}
          sx={{
            fontSize: 11,
            px: 1,
            py: 0.4,
            borderRadius: 1,
            border: "1px solid rgba(255,255,255,0.18)",
            bgcolor: "rgba(255,255,255,0.06)",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          {showGlowRailDevPopup ? "Hide GlowRail Dev" : "GlowRail Dev"}
        </Box>
      </Box> */}

      {showGlowRailDevPopup ? (
        <Box
          sx={{
            position: "absolute",
            right: 12,
            top: 56,
            zIndex: 50,
            width: 280,
            borderRadius: 1.5,
            border: "1px solid rgba(255,255,255,0.18)",
            bgcolor: "rgba(18,18,18,0.94)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
            p: 1.2,
            backdropFilter: "blur(8px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ fontSize: 12, fontWeight: 900 }}>GlowRail Dev</Box>
            <Box sx={{ display: "flex", gap: 0.75 }}>
              <Box
                onClick={logGlowRailConfig}
                sx={{
                  fontSize: 10,
                  px: 0.8,
                  py: 0.3,
                  borderRadius: 1,
                  border: "1px solid rgba(255,255,255,0.16)",
                  cursor: "pointer",
                }}
              >
                Log
              </Box>
              <Box
                onClick={resetGlowRailConfig}
                sx={{
                  fontSize: 10,
                  px: 0.8,
                  py: 0.3,
                  borderRadius: 1,
                  border: "1px solid rgba(255,255,255,0.16)",
                  cursor: "pointer",
                }}
              >
                Reset
              </Box>
            </Box>
          </Box>

          {[
            ["Top height", "heights.top", glowRailConfig.heights.top, 2, 18, 1],
            [
              "Bottom height",
              "heights.bottom",
              glowRailConfig.heights.bottom,
              2,
              18,
              1,
            ],
            [
              "Pos threshold",
              "activation.positiveThreshold",
              glowRailConfig.activation.positiveThreshold,
              0,
              1,
              0.01,
            ],
            [
              "Neg threshold",
              "activation.negativeThreshold",
              glowRailConfig.activation.negativeThreshold,
              0,
              1,
              0.01,
            ],
            [
              "Pos softness",
              "activation.positiveSoftness",
              glowRailConfig.activation.positiveSoftness,
              0.01,
              0.5,
              0.01,
            ],
            [
              "Neg softness",
              "activation.negativeSoftness",
              glowRailConfig.activation.negativeSoftness,
              0.01,
              0.5,
              0.01,
            ],
            [
              "BG threshold",
              "background.showThreshold",
              glowRailConfig.background.showThreshold,
              0,
              1,
              0.01,
            ],
            [
              "BG alpha",
              "background.alpha",
              glowRailConfig.background.alpha,
              0,
              0.25,
              0.01,
            ],
            [
              "Near alpha max",
              "glow.nearAlphaMax",
              glowRailConfig.glow.nearAlphaMax,
              0,
              1,
              0.01,
            ],
            [
              "Far alpha max",
              "glow.farAlphaMax",
              glowRailConfig.glow.farAlphaMax,
              0,
              1,
              0.01,
            ],
            [
              "Energy start",
              "energy.startThreshold",
              glowRailConfig.energy.startThreshold,
              0,
              1,
              0.01,
            ],

            [
              "Band lane height",
              "band.laneHeight",
              glowRailConfig.band.laneHeight,
              6,
              40,
              1,
            ],
            [
              "Band bleed X",
              "band.bleedX",
              glowRailConfig.band.bleedX,
              0,
              40,
              1,
            ],
            [
              "Band power",
              "band.interpolationPower",
              glowRailConfig.band.interpolationPower,
              0.6,
              2.5,
              0.05,
            ],
            ["Band blur", "band.blurPx", glowRailConfig.band.blurPx, 0, 24, 1],

            [
              "Spread energy",
              "band.spreadEnergyFactor",
              glowRailConfig.band.spreadEnergyFactor,
              0,
              16,
              0.5,
            ],
            [
              "Spread value",
              "band.spreadValueFactor",
              glowRailConfig.band.spreadValueFactor,
              0,
              16,
              0.5,
            ],
            [
              "Pos ridge inset",
              "band.positiveRidgeInset",
              glowRailConfig.band.positiveRidgeInset,
              0.4,
              2.5,
              0.05,
            ],
            [
              "Neg ridge outset",
              "band.negativeRidgeOutset",
              glowRailConfig.band.negativeRidgeOutset,
              0.4,
              2.5,
              0.05,
            ],
            [
              "Center ridge boost",
              "band.centerRidgeBoost",
              glowRailConfig.band.centerRidgeBoost,
              0.5,
              2.0,
              0.05,
            ],
          ].map(([label, path, value, min, max, step]) => (
            <Box key={String(path)} sx={{ mb: 0.9 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  mb: 0.35,
                }}
              >
                <Box>{label as string}</Box>
                <Box sx={{ opacity: 0.8 }}>{Number(value).toFixed(2)}</Box>
              </Box>
              <input
                type="range"
                min={Number(min)}
                max={Number(max)}
                step={Number(step)}
                value={Number(value)}
                onChange={(e) =>
                  setGlowRailNumber(
                    path as
                      | "heights.top"
                      | "heights.bottom"
                      | "activation.positiveThreshold"
                      | "activation.positiveSoftness"
                      | "activation.negativeThreshold"
                      | "activation.negativeSoftness"
                      | "background.showThreshold"
                      | "background.alpha"
                      | "glow.nearAlphaMax"
                      | "glow.farAlphaMax"
                      | "energy.startThreshold"
                      | "band.laneHeight"
                      | "band.bleedX"
                      | "band.interpolationPower"
                      | "band.blurPx",
                    Number(e.target.value),
                  )
                }
                style={{ width: "100%" }}
              />
            </Box>
          ))}
        </Box>
      ) : null}
      {/* 
      <Box
        sx={{
          position: "relative",
          width: rows.length * (COL_W + COL_GAP),
          minWidth: "100%",
          mb: `${glowRailConfig.band.gapBelow}px`,
          pointerEvents: "none",
        }}
      >

        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: rows.length * (COL_W + COL_GAP),
            height: glowRailConfig.band.laneHeight,
            background: topBandStyle.background,
            filter: topBandStyle.filter,
            opacity: 1,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: glowRailConfig.band.laneHeight - 1,
            width: rows.length * (COL_W + COL_GAP),
            height: 6,
            background: sharedTensionStyle.background,
            filter: sharedTensionStyle.filter,
            opacity: 1,
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: glowRailConfig.band.laneHeight + 2,
            width: rows.length * (COL_W + COL_GAP),
            height: glowRailConfig.band.laneHeight,
            background: bottomBandStyle.background,
            filter: bottomBandStyle.filter,
            opacity: 1,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: rows.length * (COL_W + COL_GAP),
            height: glowRailConfig.band.laneHeight * 2 + 2,
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <svg
            width={rows.length * (COL_W + COL_GAP)}
            height={glowRailConfig.band.laneHeight * 2 + 2}
            style={{
              display: "block",
              overflow: "visible",
            }}
          >
            {topRidgePathD ? (
              <>
                <path
                  d={topRidgePathD}
                  fill="none"
                  stroke={teamRgba(Team_0, 0.16)}
                  strokeWidth={1.45}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.45}
                />
                <path
                  d={topRidgePathD}
                  fill="none"
                  stroke={teamRgba(Team_0, 0.92)}
                  strokeWidth={0.72}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.9}
                />
              </>
            ) : null}

            {bottomRidgePathD ? (
              <>
                <path
                  d={bottomRidgePathD}
                  fill="none"
                  stroke={teamRgba(Team_1, 0.16)}
                  strokeWidth={1.45}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.45}
                />
                <path
                  d={bottomRidgePathD}
                  fill="none"
                  stroke={teamRgba(Team_1, 0.9)}
                  strokeWidth={0.72}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.88}
                />
              </>
            ) : null}
          </svg>
        </Box>

        <Box
          sx={{
            height: glowRailConfig.band.laneHeight * 2 + 2,
          }}
        />
      </Box> */}

      <Box sx={{ display: "inline-flex", gap: 0.6, alignItems: "flex-end" }}>
        {/* {perRally.map(({ r, displayScoreLabel, scoreFaded }) => { */}
        {perRally.items.map(
          ({ r, after, displayScoreLabel, scoreFaded, numShots }) => {
            const p = pressureByRallyIndex?.[r.rallyIndex];

            const hasFirefight =
              showFirefightIcon &&
              (firefightRallySet?.has(r.rallyIndex) ?? false);

            const allBadges = rallyBadgesRef.current.get(r.rallyIndex) ?? [];

            const badges = allBadges.filter((b) => {
              if (!activeBadgeFilter) return false;
              if (b.team !== activeBadgeFilter.team) return false;
              if (b.key !== activeBadgeFilter.key) return false;
              if (activeBadgeFilter.stake === "both") return true;
              return b.stake === activeBadgeFilter.stake;
            });

            const included =
              !includedRallies || includedRallies.has(r.rallyIndex);

            // const active = activeRallyIndex === r.rallyIndex;

            const isActive = activeRallyIndex === r.rallyIndex;
            const isDimmed = !included && !isActive;

            const isIncluded = includedRallies
              ? includedRallies.has(r.rallyIndex)
              : true;

            let currentlyFiltering = false;
            if (includedRallies) {
              currentlyFiltering = true;
            }

            const active = activeRallyIndex === r.rallyIndex;
            // ✅ Always render active with full-ish visibility
            const includedForDisplay = active ? true : isIncluded;

            const hasPoint = r.scoredPoint === true;

            const serveColor =
              r.servingTeam == null
                ? "rgba(255,255,255,0.12)"
                : r.servingTeam === 0
                  ? teamRgba(Team_0, 1) // "rgba(80,160,255,1)"
                  : teamRgba(Team_1, 1); //"rgba(120,220,140,1)";
            // If scoredPoint is true, point is awarded to the serving team (by definition in your current model).
            const pointAwardedTeam = getPointAwardedTeam_Calc(r);
            // hasPoint && (r.servingTeam === 0 || r.servingTeam === 1)
            //   ? r.servingTeam
            //   : null;

            const highlight0 = hasPoint && pointAwardedTeam === 0;
            const highlight1 = hasPoint && pointAwardedTeam === 1;

            const cap0 = hasPoint && highlight0;
            const cap1 = hasPoint && highlight1;

            // bar opacity: both bars pop when point happened; both fade when no point

            // additionally: non-scoring bar is slightly dimmer than scoring bar (still popped)

            // Winner marker (independent of whether a point was scored).
            const winnerColor =
              r.winnerTeam == null
                ? "rgba(255,255,255,0.18)"
                : r.winnerTeam === 0
                  ? teamRgba(Team_0, 0.95) // "rgba(80,160,255,0.95)"
                  : teamRgba(Team_1, 0.95); // "rgba(120,220,140,0.95)";

            let team0_Border = "1px solid transparent";
            let team1_Border = "1px solid transparent";
            if (r.winnerTeam === 0) {
              team0_Border = "1px solid rgba(218,165,32,0.95)";
            }
            if (r.winnerTeam === 1) {
              team1_Border = "1px solid rgba(218,165,32,0.95)";
            }

            return (
              <Box
                key={"rally-" + r.rallyIndex}
                data-demo={`timeline-rally-${r.rallyIndex + 1}`}
                data-rally-index={r.rallyIndex}
                data-active={active ? "true" : "false"}
                // @ts-ignore
                ref={(el: HTMLDivElement | null) => {
                  if (el) localRefs.current.set(r.rallyIndex, el);
                  else localRefs.current.delete(r.rallyIndex);

                  registerRef?.(r.rallyIndex, el);
                }}
                onClick={() => onPickRally(r.rallyIndex)}
                sx={{
                  opacity: isDimmed ? DIM_OPACITY : 1,
                  filter: isDimmed ? DIM_GRAYSCALE : "none",
                  // transform: included ? "none" : "scale(0.995)",
                  transform: isDimmed ? "scale(0.995)" : "none",

                  pointerEvents: "auto", // included ? "auto" : "auto", // still clickable if you want

                  width: 18,
                  height: colH,
                  borderRadius: 1.2,
                  cursor: "pointer",
                  userSelect: "none",
                  border: active
                    ? "2px solid rgba(255,255,255,0.92)"
                    : currentlyFiltering && includedForDisplay
                      ? "2px solid black"
                      : "1px solid rgba(255,255,255,0.18)",
                  // bgcolor: active
                  //   ? "rgba(255,255,255,0.10)"
                  //   : "rgba(0,0,0,0.08)",
                  bgcolor: active
                    ? // @ts-ignore
                      teamRgba(r.servingTeam, 0.1) //  "rgba(255,255,255,0.10)"
                    : // @ts-ignore
                      teamRgba(r.servingTeam, 0.1), //   "rgba(0,0,0,0.08)",
                  boxShadow: active
                    ? "0 0 0 3px rgba(80,160,255,0.20), 0 10px 24px rgba(0,0,0,0.35)"
                    : "none",
                  transition:
                    "opacity 140ms ease, filter 140ms ease, transform 140ms ease, " +
                    "box-shadow 120ms ease, background 120ms ease, border 120ms ease",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginLeft: "4px",
                  overflow: "visible",
                  paddingRight: "2px",
                  paddingLeft: "2px",
                  paddingBottom: "4px",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: active
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(255,255,255,0.15)",
                    boxShadow: active
                      ? "0 0 0 1px rgba(255,255,255,0.05) inset"
                      : "0 0 0 1px rgba(255,255,255,0.035) inset",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    opacity: includedForDisplay ? 1 : 0.99,
                    filter: includedForDisplay ? "none" : "grayscale(90%)",
                    transform: includedForDisplay ? "none" : "scale(1)",
                    transition:
                      "opacity 140ms ease, filter 140ms ease, transform 140ms ease",

                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    lineHeight: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      opacity: 0.85,
                      fontWeight: 900,
                      lineHeight: 1,
                    }}
                  >
                    {r.rallyIndex + 1}
                  </Typography>
                </Box>

                {hasFirefight ? (
                  <Box
                    sx={{
                      position: "absolute",
                      // top: 16,
                      top: 40,
                      zIndex: 3,
                      fontSize: 11,
                      lineHeight: 1,
                      opacity: 0.9,
                      pointerEvents: "none",
                      textShadow: "0 1px 2px rgba(0,0,0,0.65)",
                      userSelect: "none",
                      textAlign: "center",
                    }}
                  >
                    {firefightIcon}
                  </Box>
                ) : null}

                {/* middle markers */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.4,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      mt: 0.25,
                      display: "flex",
                      justifyContent: "center",
                      gap: 0.35,
                      flexWrap: "wrap",
                      minHeight: 14, // reserves space even if none
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        // ✅ clamp the badge lane so it can’t grow vertically
                        height: barsAreaHeightPx, // 14px badges + 2px breathing room
                        minHeight: barsAreaHeightPx,
                        maxHeight: barsAreaHeightPx,
                        overflow: "hidden",

                        // ✅ keep badges on one line
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.35,
                        flexWrap: "nowrap",
                        whiteSpace: "nowrap",
                        position: "absolute",
                        top: 2,
                        left: "50%",
                        transform: "translateX(-50%)", // ✅ true centering
                        paddingBottom: "4px",
                        zIndex: 5, // ✅ ensure hover hits badges, not bars
                        pointerEvents: "auto",
                      }}
                    >
                      {badges.map((b, i) => {
                        const teamColor = teamRgba(
                          b.team === 0 ? Team_0 : Team_1,
                          0.95,
                        );
                        const teamBorder = teamRgba(b.team, 0.22); // 👈 subtle

                        const Icon =
                          STORY_FILTER_ICON[b.key] ?? ErrorOutlineIcon;
                        const rgba = teamRgba(
                          b.team === 0 ? Team_0 : Team_1,
                          0.95,
                        );

                        return (
                          <Tooltip
                            enterDelay={0}
                            leaveDelay={0}
                            disableInteractive
                            disableHoverListener={false}
                            disableFocusListener
                            disableTouchListener
                            // PopperProps={{
                            //   disablePortal: true, // optional; helps debugging/behavior sometimes
                            // }}
                            PopperProps={{
                              modifiers: [
                                { name: "eventListeners", enabled: true },
                              ],
                            }}
                            slotProps={{
                              popper: {
                                sx: {
                                  pointerEvents: "none", // <- tooltip overlay ignores mouse
                                },
                              },
                            }}
                            key={`${b.stake}-${b.key}-${b.team}-${i}`}
                            arrow
                            title={
                              <Box
                                sx={{ fontSize: 16 }}
                              >{`${b.stake === "opportunityLost" ? "Opportunity lost" : "Game point lost"}: ${b.key}`}</Box>
                            }
                          >
                            <Box
                              onMouseEnter={() => {
                                //working console.error("mouse enter:" + b.key);
                                onHoverBadge?.(b);
                              }}
                              onMouseLeave={() => {
                                //working console.error("mouse leave:" + b.key);
                                onHoverBadge?.(null);
                              }}
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "4px",
                                bgcolor: "rgba(255,255,255,0.12)", // ✅ light chip
                                border: `1px solid ${teamBorder}`, // ✅ team-colored border
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 0 0 1px rgba(0,0,0,0.08)", // subtle
                              }}
                            >
                              <Box sx={iconChip(b.team)}>
                                <Box
                                  sx={{
                                    fontSize: 14,
                                    color: teamColor,
                                    display: "inline-flex",
                                  }}
                                >
                                  {/* <Icon /> */}
                                  {React.createElement(
                                    Icon as React.ElementType,
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* bars + floating score label (label hugs the top of the taller bar) */}
                  <Box
                    className="floating-score"
                    sx={{
                      height: barsH,
                      width: "100%",
                      position: "relative",
                      display: "flex",
                      alignItems: "stretch", // "flex-end",
                      justifyContent: "center",
                      boxSizing: "border-box",
                    }}
                  >
                    {(() => {
                      const max = Math.max(1, perRally.maxScoreEnd || 1);
                      const t0 = Number(after?.[0] ?? 0);
                      const t1 = Number(after?.[1] ?? 0);

                      const h0 = Math.max(
                        2,
                        Math.round((t0 / max) * barsH) - 12,
                      );
                      const h1 = Math.max(
                        2,
                        Math.round((t1 / max) * barsH) - 12,
                      );
                      const tallest = Math.max(h0, h1);

                      // label floats a few px above tallest bar
                      const labelTopPx = barsH - tallest - 14; // 14 ~= label height + padding
                      const labelTop = Math.max(0, labelTopPx);

                      return (
                        <>
                          {/* score label: ONLY when point happened */}
                          {hasPoint ? (
                            <Typography
                              sx={{
                                position: "absolute",
                                top: `${labelTop}px`,
                                left: 0,
                                right: 0,
                                textAlign: "center",
                                fontSize: 10,
                                fontWeight: 900,
                                opacity: 0.92,
                                lineHeight: 1,
                                pointerEvents: "none",
                              }}
                            >
                              {displayScoreLabel}
                            </Typography>
                          ) : null}

                          {/* bars row */}
                          <Box
                            sx={{
                              height: barsH,
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "flex-end",
                              justifyContent: "center",
                              gap: 0.4,
                            }}
                          >
                            <Box
                              className="team0-bar"
                              data-team="0"
                              sx={{
                                width: 6,
                                height: h0,
                                borderRadius: 1,
                                bgcolor: teamRgba(Team_0, 0.85), // "rgba(80,160,255,0.85)",
                                boxShadow:
                                  r.winnerTeam === 0
                                    ? boxShadowRallyWinner
                                    : "none",
                                //border: team0_Border, // "1px solid rgba(0,0,0,0.28)",
                                // outline: highlight0 ? outlineScore : outlineNeutral,
                                opacity: hasPoint
                                  ? highlight0
                                    ? 1
                                    : 0.7
                                  : 0.2,
                                position: "relative",
                                ...(cap0
                                  ? {
                                      "&:before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: `${GOLD_CAP_H}px`,
                                        background: goldCapBg,
                                        boxShadow: goldGlow,
                                      },
                                    }
                                  : null),
                              }}
                            />
                            <Box
                              className="team1-bar"
                              data-team="1"
                              sx={{
                                width: 6,
                                height: h1,
                                borderRadius: 1,
                                bgcolor: teamRgba(Team_1, 1), //(  "rgba(120,220,140,0.80)",
                                boxShadow:
                                  r.winnerTeam === 1
                                    ? boxShadowRallyWinner
                                    : "none",

                                // outline: highlight1 ? outlineScore : outlineNeutral,
                                opacity: hasPoint
                                  ? highlight1
                                    ? 1
                                    : 0.7
                                  : 0.2,
                                position: "relative",
                                ...(cap1
                                  ? {
                                      "&:before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: `${GOLD_CAP_H}px`,
                                        background: goldCapBg,
                                        boxShadow: goldGlow,
                                      },
                                    }
                                  : null),
                              }}
                            />
                          </Box>
                        </>
                      );
                    })()}
                  </Box>

                  {/* winner marker (always shows winnerTeam if present) */}
                  {/* winner marker: star in team color */}
                  <Box
                    data-team={r.winnerTeam}
                    sx={{
                      marginBottom: "2px",
                      width: 10,
                      height: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: r.winnerTeam == null ? 0.35 : 1,
                      userSelect: "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        lineHeight: 1,
                        fontWeight: 900,
                        color: winnerColor,
                        // subtle outline so it reads on light backgrounds without becoming noisy
                        textShadow: "0 0 1px rgba(0,0,0,0.35)",
                      }}
                    >
                      ★
                    </Typography>
                  </Box>

                  <span data-team={r.servingTeam}>
                    <ServePaddleIcon
                      color={serveColor}
                      dim={r.servingTeam == null}
                    />
                  </span>

                  {(() => {
                    const n = typeof numShots === "number" ? numShots : null;
                    const isShort = n != null && n <= 2;
                    const isMax =
                      n != null &&
                      perRally.maxShots > 0 &&
                      n === perRally.maxShots;

                    return (
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontStyle: "italic",
                          lineHeight: 1,
                          mt: "1px",
                          userSelect: "none",

                          // baseline
                          opacity: 0.55,

                          ...(isShort
                            ? {
                                opacity: 0.95,
                                fontWeight: 900,
                                bgcolor: "rgba(255,255,255,0.18)",
                                color: "red",
                                px: "3px",
                                borderRadius: "6px",
                              }
                            : null),

                          ...(isMax
                            ? {
                                opacity: 1,
                                fontWeight: 900,
                                bgcolor: "rgba(255,255,255,0.26)",
                                px: "3px",
                                borderRadius: "6px",
                                textDecoration: "underline",
                              }
                            : null),
                        }}
                      >
                        {n ?? "—"}
                      </Typography>
                    );
                  })()}

                  {(() => {
                    const n = typeof numShots === "number" ? numShots : null;
                    const isShort = n != null && n <= 2;
                    const isMax =
                      n != null &&
                      perRally.maxShots > 0 &&
                      n === perRally.maxShots;

                    return (
                      <Typography
                        sx={{
                          display: "none",
                          fontSize: 10,
                          fontStyle: "italic",
                          lineHeight: 1,
                          mt: "1px",
                          userSelect: "none",

                          // baseline
                          opacity: 0.55,

                          ...(isShort
                            ? {
                                opacity: 0.95,
                                fontWeight: 900,
                                bgcolor: "rgba(255,255,255,0.18)",
                                color: "red",
                                px: "3px",
                                borderRadius: "6px",
                              }
                            : null),

                          ...(isMax
                            ? {
                                opacity: 1,
                                fontWeight: 900,
                                bgcolor: "rgba(255,255,255,0.26)",
                                px: "3px",
                                borderRadius: "6px",
                                // textDecoration: "underline",
                              }
                            : null),
                        }}
                      >
                        {((r.segmentEndMs - r.segmentStartMs) / 1000).toFixed(
                          0,
                        ) + "s"}
                      </Typography>
                    );
                  })()}
                </Box>
              </Box>
            );
          },
        )}
      </Box>
    </Box>
  );
}
